import { lazy, useEffect } from 'react';

import { getTimezone, IAddress } from '@sureapp/platform-sdk';
import { getGeocodedNormalizedAddress } from '@sureapp/wl-toggle-common/dist/utils/utils';
import omit from 'lodash/omit';
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useRouteMatch,
} from 'react-router-dom';

import api from '@@/api';
import { useAppDispatch, useAppSelector } from '@@/auto/hooks/redux';
import { TGetLinksQuote } from '@@/auto/interfaces';
import { clearOktaForQA, clearQuote } from '@@/auto/store/actions';
import QAAutoQuickPurchaseScreen from '@@/common/components/__qa_only__/QAAutoQuickPurchaseScreen';
import QAQuickMenu from '@@/common/components/__qa_only__/QAQuickMenu';
import { useConfig } from '@@/common/services/useConfig';
import withGlobals, { TWithGlobals } from '@@/withGlobals';

import { QAStartScreensContextProvider } from './qaStartScreensContext';

const QAAutoStartScreen = lazy(
  () => import('@@/common/components/__qa_only__/QAAutoStartScreen')
);
const QARentersStartScreen = lazy(
  () => import('@@/common/components/__qa_only__/QARentersStartScreen')
);

type TQAAutoStartScreensContainer = {} & Pick<TWithGlobals, 'onError'>;

function QAAutoStartScreensContainer({
  onError,
}: TQAAutoStartScreensContainer) {
  const {
    config: {
      featureFlags: {
        isAutoQuoteLinkExchangeEnabled,
        isRentersQuoteCreationEnabled,
      },
      tenantDisplayName,
    },
  } = useConfig();

  const dispatch = useAppDispatch();
  const history = useHistory();
  const match = useRouteMatch();

  const quote = useAppSelector((state) => state.quote);
  const quoteData = useAppSelector((state) => state.quoteData);
  const hasOktaInfo = useAppSelector(
    (state) =>
      !!state.oktaLoginInfo ||
      !!state.oktaAccessToken ||
      !!state.oktaIdToken ||
      !!state.authToken
  );

  useEffect(
    function resetQuote() {
      if (
        !!quote ||
        !!quoteData.partnerData ||
        !!quoteData.hasSeenTenantDiscountToast
      ) {
        clearQuote()(dispatch);
      }
    },
    [dispatch, quote, quoteData]
  );

  useEffect(
    function resetLoginInfo() {
      if (hasOktaInfo) {
        clearOktaForQA()(dispatch);
      }
    },
    [dispatch, hasOktaInfo]
  );

  function getAddressObject({
    streetAddress,
    addressLine2,
  }: {
    streetAddress: string;
    addressLine2: string;
  }): Promise<IAddress> {
    return getGeocodedNormalizedAddress(streetAddress, {
      withLatLng: true,
    }).then((address) => {
      return getTimezone(address.latitude, address.longitude).then(
        ({ timeZoneId }) => {
          const cleanAddress = omit(address, [
            'latitude',
            'longitude',
          ]) as IAddress;
          return {
            ...cleanAddress,
            line2: addressLine2 || null,
            timezone: timeZoneId,
          };
        }
      );
    });
  }

  function getLinks(getLinksQuote: TGetLinksQuote) {
    return new Promise<void>((resolve, reject) => {
      return api
        .getLinkUrl(getLinksQuote)
        .then(({ link_url: nextUrl }) => {
          if (
            window.location.hostname === 'localhost' ||
            window.location.hostname.indexOf('sure.town') !== -1
          ) {
            history.push(nextUrl.split('.com')[1]);
          } else {
            window.location.href = nextUrl;
          }
          resolve();
        })
        .catch((error) => {
          onError(error);
          reject();
        });
    });
  }

  return (
    <>
      <Switch>
        {isAutoQuoteLinkExchangeEnabled && (
          <Route
            exact
            path={`${match.path}auto/start`}
            render={() => (
              <>
                <QAAutoStartScreen
                  {...{ getAddressObject, getLinks, tenantDisplayName }}
                />
                <QAQuickMenu vertical="auto" />
              </>
            )}
          />
        )}
        {isRentersQuoteCreationEnabled && (
          <Route
            exact
            path={`${match.path}renters/start`}
            render={() => (
              <>
                <QARentersStartScreen
                  getAddressObject={getAddressObject}
                  getLinks={getLinks}
                />
                <QAQuickMenu vertical="renters" />
              </>
            )}
          />
        )}
        <Route
          exact
          path={`${match.path}auto/quick-purchase`}
          render={() => (
            <QAStartScreensContextProvider>
              <QAAutoQuickPurchaseScreen />
              <QAQuickMenu vertical="auto" />
            </QAStartScreensContextProvider>
          )}
        />
        <Redirect to="/" />
      </Switch>
    </>
  );
}

export default withGlobals(QAAutoStartScreensContainer);
